/**
 * Member timezones for the calendar's time-of-day filter.
 *
 * Ported from the Little Sisters dashboard. Values are IANA names that both
 * Postgres `AT TIME ZONE` and JS `Intl` understand, ordered roughly west → east
 * then the Americas. Many cities share a clock but are listed separately so a
 * member's own city can be found by name. Add a city here if a zone is missing.
 */
export const TIMEZONES: ReadonlyArray<readonly [string, string]> = [
  ["UTC", "UTC"],
  // Europe
  ["Europe/London", "UK · London"],
  ["Europe/Dublin", "Ireland · Dublin"],
  ["Europe/Lisbon", "Portugal · Lisbon"],
  ["Europe/Paris", "France · Paris"],
  ["Europe/Madrid", "Spain · Madrid"],
  ["Europe/Amsterdam", "Netherlands · Amsterdam"],
  ["Europe/Rome", "Italy · Rome"],
  ["Europe/Berlin", "Germany · Berlin"],
  ["Europe/Zurich", "Switzerland · Zurich"],
  ["Europe/Prague", "Czechia · Prague"],
  ["Europe/Bratislava", "Slovakia · Bratislava"],
  ["Europe/Budapest", "Hungary · Budapest"],
  ["Europe/Warsaw", "Poland · Warsaw"],
  ["Europe/Belgrade", "Serbia · Belgrade"],
  ["Europe/Zagreb", "Croatia · Zagreb"],
  ["Europe/Athens", "Greece · Athens"],
  ["Europe/Bucharest", "Romania · Bucharest"],
  ["Europe/Sofia", "Bulgaria · Sofia"],
  ["Europe/Vilnius", "Lithuania · Vilnius"],
  ["Europe/Kiev", "Ukraine · Kyiv"],
  ["Europe/Istanbul", "Turkey · Istanbul"],
  ["Europe/Moscow", "Russia · Moscow"],
  // Africa & Middle East
  ["Africa/Casablanca", "Morocco · Casablanca"],
  ["Africa/Cairo", "Egypt · Cairo"],
  ["Asia/Jerusalem", "Israel · Jerusalem"],
  ["Asia/Riyadh", "Saudi Arabia · Riyadh"],
  ["Asia/Dubai", "Gulf · Dubai"],
  // Asia
  ["Asia/Kolkata", "India · Kolkata"],
  ["Asia/Bangkok", "Thailand · Bangkok"],
  ["Asia/Kuala_Lumpur", "Malaysia · KL"],
  ["Asia/Singapore", "Singapore"],
  ["Asia/Shanghai", "China · Shanghai"],
  ["Asia/Taipei", "Taiwan · Taipei"],
  ["Asia/Seoul", "Korea · Seoul"],
  ["Asia/Tokyo", "Japan · Tokyo"],
  // Oceania
  ["Australia/Perth", "Australia · Perth"],
  ["Australia/Adelaide", "Australia · Adelaide"],
  ["Australia/Brisbane", "Australia · Brisbane"],
  ["Australia/Sydney", "Australia · Sydney"],
  ["Australia/Melbourne", "Australia · Melbourne"],
  // Americas
  ["America/Sao_Paulo", "Brazil · São Paulo"],
  ["America/Buenos_Aires", "Argentina · Buenos Aires"],
  ["America/Santiago", "Chile · Santiago"],
  ["America/Bogota", "Colombia · Bogotá"],
  ["America/Panama", "Panama"],
  ["America/New_York", "US East · New York"],
  ["America/Toronto", "Canada · Toronto/Montreal"],
  ["America/Chicago", "US Central · Chicago"],
  ["America/Mexico_City", "Mexico · Mexico City"],
  ["America/Los_Angeles", "US West · Los Angeles"],
];

export const UTC_TZ = "UTC";

/** Human label for a timezone value ("Europe/London" → "UK · London"). */
export const tzLabel = (tz: string): string => TIMEZONES.find(([value]) => value === tz)?.[1] ?? tz;

/** Parse a UTC clock value to [hours, minutes]; handles "18:00", "18:00:00Z", "6:00 PM". */
const parseUtcHM = (timeUtc: string): [number, number] | null => {
  if (!timeUtc) {
    return null;
  }
  const ampm = String(timeUtc).match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (ampm) {
    let hours = Number(ampm[1]);
    const minutes = Number(ampm[2]);
    if (/PM/i.test(ampm[3]) && hours !== 12) {
      hours += 12;
    }
    if (/AM/i.test(ampm[3]) && hours === 12) {
      hours = 0;
    }
    return [hours, minutes];
  }
  const hm = String(timeUtc).match(/(\d{1,2}):(\d{2})/);
  return hm ? [Number(hm[1]), Number(hm[2])] : null;
};

const fmtHM = (hours: number, minutes: number): string =>
  `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;

/**
 * A fixed UTC hour/minute rendered as the wall clock in `tz`. Uses today's date
 * as the reference so the offset (including DST) is current.
 */
const localFromUTC = (hours: number, minutes: number, tz: string): string => {
  const now = new Date();
  const at = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), hours, minutes),
  );
  return at.toLocaleTimeString("en-GB", {
    timeZone: tz,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

/** An event's UTC clock string → the wall clock a member in `tz` sees. */
export const evLocal = (timeUtc: string, tz: string): string => {
  const hm = parseUtcHM(timeUtc);
  if (!hm) {
    return timeUtc;
  }
  return !tz || tz === UTC_TZ ? fmtHM(hm[0], hm[1]) : localFromUTC(hm[0], hm[1], tz);
};

/**
 * A timezone's current UTC offset in minutes (DST-aware). e.g. Europe/Paris in
 * summer → 120. Matches the offset Postgres `pg_timezone_names.utc_offset` gives,
 * so "everyone on that clock" lines up on both sides.
 */
export const tzOffsetMinutes = (tz: string): number => {
  if (!tz || tz === UTC_TZ) {
    return 0;
  }
  const now = new Date();
  const local = new Date(now.toLocaleString("en-US", { timeZone: tz }));
  const utc = new Date(now.toLocaleString("en-US", { timeZone: UTC_TZ }));
  return Math.round((local.getTime() - utc.getTime()) / 60000);
};

/** A "HH:MM" UTC boundary → the wall clock in `tz` (for zone-range labels). */
export const zoneLocal = (hhmm: string, tz: string): string => {
  if (!tz || tz === UTC_TZ) {
    return hhmm;
  }
  const [hours, minutes] = hhmm.split(":").map(Number);
  return localFromUTC(hours, minutes, tz);
};
